import { useEffect } from "react";

import i18n from "@/lib/i18n";
import { DEFAULT_LANGUAGE, normalizeLanguage } from "@/lib/i18n/languages";
import { useLanguage } from "@/lib/i18n/useLanguage";

/**
 * Keeps `<html lang>` in step with the active language, and applies the
 * language saved on the user's account.
 *
 * `index.html` ships a static `lang="en"`. Left alone it lies to everything
 * that reads it: screen readers pick the wrong pronunciation rules, the browser
 * offers to translate a page that is already translated, and CSS hyphenation
 * and `:lang()` selectors break.
 *
 * The `useLanguage()` call is what makes the saved language follow a user
 * between browsers: this component is mounted app-wide, so hydration happens on
 * any page. Hanging it off the settings picker instead would only apply the
 * account's language once the user opened Settings. The underlying preferences
 * query is shared with the settings page and the notification hooks, and is
 * skipped outright in preview mode and before a workspace is chosen.
 *
 * Renders nothing — mounted once, near the top of the tree in `App.tsx`.
 */
export function LanguageSync(): null {
  useLanguage();

  useEffect(() => {
    const apply = (next: string) => {
      document.documentElement.lang = normalizeLanguage(next) ?? DEFAULT_LANGUAGE;
    };
    apply(i18n.language);
    i18n.on("languageChanged", apply);
    return () => {
      i18n.off("languageChanged", apply);
    };
  }, []);

  return null;
}

export default LanguageSync;
